import React, {useState, useRef, useEffect, useMemo} from 'react';
import {useMutation} from 'react-query';
import {toast} from 'react-hot-toast';
import {useTranslation} from 'react-i18next';

import useStore from '@context/useStore';
import {updateStoreApi, downloadStoreQRCode} from '@apis';

import {ContentWrapper, Input, Card} from '@components';

// Store name character limit based on backend model
const STORE_NAME_MAX_LENGTH = 191;

// Complete allowed image types including SVG
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/svg+xml',
];

const StoreSetup = () => {
  const {t} = useTranslation();
  const upload = useRef(null);
  const fileReaderRef = useRef(null);
  const {storeDetails, refetch} = useStore();
  const [isEditedView, setEditedView] = useState(false);
  const [storeNameError, setStoreNameError] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  // Form state - these hold the current form values
  const [storeName, setStoreName] = useState('');
  const [originalLogoFile, setOriginalLogoFile] = useState(null);

  // Separate preview state that doesn't get reset
  const [logoPreview, setLogoPreview] = useState('');

  // Flag to track if we've initialized from server data
  const [hasInitialized, setHasInitialized] = useState(false);

  // Simple unique ID for file input
  const fileInputId = useMemo(() => {
    return `upload-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  }, []);

  // Initialize state from store details ONLY when component first mounts or storeDetails first loads
  useEffect(() => {
    if (storeDetails && !hasInitialized) {
      setStoreName(storeDetails.name || '');
      setLogoPreview(storeDetails.logo || '');
      setHasInitialized(true);
    }
  }, [storeDetails, hasInitialized]);

  // Cleanup FileReader on unmount
  useEffect(() => {
    return () => {
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
    };
  }, []);

  const updateStore = useMutation(params => updateStoreApi(params), {
    onSuccess: async data => {
      await toast.success(t('storeSetup.storeUpdated'));

      // Update our local state with the response data instead of refetching
      if (data.name !== undefined) {
        setStoreName(data.name);
      }

      // If we uploaded a new logo, update the preview with the server response
      if (data.logo) {
        setLogoPreview(data.logo);
      }

      // Reset file upload state
      if (upload.current) {
        upload.current.value = '';
      }
      setOriginalLogoFile(null);

      // Reset edited view
      setEditedView(false);

      // Refetch in background to ensure context is updated, but don't wait for it
      refetch();
    },
    onError: async err => {
      toast.error(t('storeSetup.updateError'));
    },
  });

  const handleStoreNameChange = e => {
    const newValue = e.target.value || '';

    // Allow input only if under the max length
    if (newValue.length <= STORE_NAME_MAX_LENGTH) {
      setStoreName(newValue);
      setStoreNameError('');
      setEditedView(true);
    } else {
      setStoreNameError(
        t('storeSetup.maxLengthExceeded', {maxLength: STORE_NAME_MAX_LENGTH}),
      );
    }
  };

  // Enhanced file upload handler that preserves form state and prevents race conditions
  const handleLogoUpload = async e => {
    const file = e.target.files[0];

    if (!file) return;

    // Prevent concurrent file processing
    if (isProcessingFile || updateStore.isLoading) {
      toast.error(t('storeSetup.updateError'));
      e.target.value = '';
      return;
    }

    setIsProcessingFile(true);

    // Validate file type including SVG
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(t('storeSetup.invalidFileType'));
      e.target.value = '';
      setIsProcessingFile(false);
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error(t('storeSetup.imageSizeError'));
      e.target.value = '';
      setIsProcessingFile(false);
      return;
    }

    // Store the original file for API submission
    setOriginalLogoFile(file);

    // Create preview immediately with proper cleanup
    try {
      // Clean up any existing FileReader
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }

      const reader = new FileReader();
      fileReaderRef.current = reader;

      reader.onload = event => {
        if (event.target === fileReaderRef.current) {
          // Ensure this is the current reader
          setLogoPreview(event.target.result);
          setEditedView(true);
          setIsProcessingFile(false);
        }
      };

      reader.onerror = error => {
        console.error('Error creating logo preview:', error);
        toast.error(t('storeSetup.updateError'));
        e.target.value = '';
        setIsProcessingFile(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(t('storeSetup.updateError'));
      e.target.value = '';
      setIsProcessingFile(false);
    }
  };

  const handleStoreUpdate = async () => {
    // Prevent double submissions
    if (updateStore.isLoading || isProcessingFile) {
      return;
    }

    // Validate store name before update
    if ((storeName || '').length > STORE_NAME_MAX_LENGTH) {
      setStoreNameError(
        t('storeSetup.maxLengthExceeded', {maxLength: STORE_NAME_MAX_LENGTH}),
      );
      return;
    }

    // Prepare update data - only include fields that have changed
    const updateData = {};

    // Always include store name if it has changed
    if (storeName !== storeDetails?.name) {
      updateData.name = storeName;
    }

    // Only include logo if user uploaded a new one
    if (originalLogoFile) {
      updateData.logo = originalLogoFile;
    }

    // Include other store details that might be needed for validation
    // but only if we're not updating them
    if (!updateData.name && !originalLogoFile) {
      // If neither name nor logo changed, we shouldn't be here
      // but include current values to be safe
      updateData.name = storeName;
    }

    await updateStore.mutate(updateData);
  };

  const handleCancel = () => {
    // Reset form fields to original values from storeDetails
    setStoreName(storeDetails?.name || '');
    setLogoPreview(storeDetails?.logo || '');

    // Clear file upload state
    setOriginalLogoFile(null);
    if (upload.current) {
      upload.current.value = '';
    }

    // Clear error states
    setStoreNameError('');

    // Clear processing states
    setIsProcessingFile(false);

    // Abort any ongoing file reader operation
    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
    }

    // Reset edited view
    setEditedView(false);
  };

  const downloadQRCode = async () => {
    try {
      // Get the store ID from the context
      const storeId = storeDetails?.id;
      if (!storeId) {
        throw new Error('Store ID not found');
      }

      // Use the download-qr endpoint through the API method
      const response = await downloadStoreQRCode(storeId);
      if (!response.ok) {
        throw new Error('Failed to fetch QR code');
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Safely parse filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `store-qr-${storeDetails?.store_uuid || 'code'}.png`; // Default with extension

      if (contentDisposition && contentDisposition.includes('filename=')) {
        try {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        } catch (parseError) {
          console.warn(
            'Could not parse filename from Content-Disposition header, using default',
          );
        }
      }

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error(t('storeSetup.downloadError'));
    }
  };

  // Determine if form can be submitted
  const canSubmit = !updateStore.isLoading && !isProcessingFile && isEditedView;

  return (
    <>
      {/* Screen reader announcements for status changes */}
      <div
        role="status"
        aria-live="polite"
        className="absolute left-[-10000px] w-1 h-1 overflow-hidden">
        {isProcessingFile && t('storeSetup.processingFile')}
        {updateStore.isLoading && t('storeSetup.updatingStore')}
      </div>

      <ContentWrapper
        breadcrumb={[
          {title: t('sidebar.menuItems.storeSetup'), link: '/store-setup'},
        ]}
        onButtonOneClick={canSubmit ? handleStoreUpdate : undefined}
        onButtonTwoClick={canSubmit ? handleCancel : undefined}
        hasSearch={false}
        isEditedView={canSubmit}>
        <div className="max-w-md">
          {' '}
          {/* Restrict the width of the store name section */}
          <Input
            value={storeName || ''}
            onChange={handleStoreNameChange}
            label={t('storeSetup.storeName')}
            maxWidth="100%"
            maxLength={STORE_NAME_MAX_LENGTH}
            disabled={updateStore.isLoading}
          />
          <div className="flex justify-between items-center mt-1">
            <p
              className={`text-xs ${
                storeNameError ? 'text-red' : 'text-gray-400'
              }`}>
              {storeNameError ||
                `${storeName?.length || 0}/${STORE_NAME_MAX_LENGTH} ${t(
                  'storeSetup.characters',
                )}`}
            </p>
          </div>
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
          <Card title={t('storeSetup.storeQRCode')}>
            {storeDetails?.qr_code && (
              <div>
                <img
                  className="store-logo"
                  src={storeDetails?.qr_code}
                  alt="Store QR Code"
                />
                <button
                  onClick={downloadQRCode}
                  disabled={updateStore.isLoading}
                  style={{
                    color: updateStore.isLoading ? '#ccc' : '#990820',
                    display: 'block',
                    paddingTop: 10,
                    textAlign: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: updateStore.isLoading ? 'not-allowed' : 'pointer',
                    margin: '0 auto',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                  }}>
                  {t('storeSetup.downloadImage')}
                </button>
              </div>
            )}
          </Card>
          <Card
            title={t('storeSetup.storeLogo')}
            rightContent={
              <div className="flex p-2">
                <input
                  ref={upload}
                  type="file"
                  id={fileInputId}
                  hidden
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={handleLogoUpload}
                  disabled={isProcessingFile || updateStore.isLoading}
                  aria-label={t('storeSetup.uploadLogo')}
                  aria-describedby={`${fileInputId}-description`}
                />
                <label
                  htmlFor={fileInputId}
                  className={`text-red text-xs uppercase cursor-pointer ${
                    isProcessingFile || updateStore.isLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  role="button"
                  tabIndex={isProcessingFile || updateStore.isLoading ? -1 : 0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (
                        !isProcessingFile &&
                        !updateStore.isLoading &&
                        upload.current
                      ) {
                        upload.current.click();
                      }
                    }
                  }}>
                  {isProcessingFile
                    ? t('storeSetup.processing')
                    : updateStore.isLoading
                    ? t('storeSetup.saving')
                    : t('storeSetup.uploadLogo')}
                </label>
                <div
                  id={`${fileInputId}-description`}
                  className="absolute left-[-10000px] w-1 h-1 overflow-hidden">
                  {t('storeSetup.uploadLogoDescription')}
                </div>
              </div>
            }>
            {logoPreview && (
              <div>
                <img
                  className="store-logo"
                  src={logoPreview}
                  alt="Store Logo"
                />
              </div>
            )}
            {originalLogoFile && (
              <div className="flex justify-center items-center">
                <p className="text-xs text-gray-400 ">
                  {originalLogoFile.name}
                </p>
              </div>
            )}
            {(isProcessingFile || updateStore.isLoading) && (
              <div className="flex justify-center items-center mt-2">
                <p className="text-xs text-blue-500">
                  {isProcessingFile
                    ? t('storeSetup.processingFile')
                    : t('storeSetup.saving')}
                </p>
              </div>
            )}
          </Card>
        </div>
      </ContentWrapper>
    </>
  );
};

export default StoreSetup;
